// React & Hooks
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDefinition, ReactTabulator } from 'react-tabulator';
import type { Tabulator } from 'tabulator-tables';
import MultiSelect from '@/domains/scenario/components/MultiSelect/MultiSelect';
// Custom Hooks
import useScenarioManagement from '@/domains/scenario/hooks/useScenarioManagement';
// Type
import type {
  ButtonState,
  OptionItem,
  ScenarioInputForm,
  SelectedScenario,
  Site,
} from '@/domains/scenario/types/scenario-management.types';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
// Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import TablePagination from '@/shared/components/ui/TablePagination/TablePagination';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import useRoadManagement from '@/shared/hooks/useRoadManagement';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import useSiteManagement from '@/shared/hooks/useSiteManagement';
import useUnmappedSiteRoad from '@/shared/hooks/useUnmappedSiteRoad';

const commonColumnSettings = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false,
};

// Columns whose `field` matches the backend allowed-sort whitelist keys.
// scenario endpoint allows: id (scenario_id) | updated_time.
const SCENARIO_SORT_FIELD_MAP: Record<string, string> = {
  scenario_id: 'id',
  updated_time: 'updated_time',
};

const ScenarioManagement = () => {
  const { t } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const hasChangesCreateRef = useRef<boolean>(hasChangesCreate);
  const hasChangesUpdateRef = useRef<boolean>(hasChangesUpdate);
  const isNewClickedRef = useRef<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const [newId, setNewId] = useState<string>('');

  const [siteId, setsiteId] = useState<string>(''); //사이트 유형 - 기본 모든
  const [roadId, setRoadId] = useState<string>(''); //접근로 유형 - 기본 모든
  const [facilityType, setFacilityType] = useState<string>(''); //시설물 유형 - 기본 모든
  const [optionParams, _setOptionParams] = useState<string>('upper-code=221&upper-code=EVT_TP');
  const [_unmappedSiteRoadList, setUnmappedSiteRoadList] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | number>('');
  const [selectedRoadId, setSelectedRoadId] = useState<string | number>('');
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | number>('');
  const [selectedDetectionId, setSelectedDetectionId] = useState<(string | number)[]>([]);
  const [selectedDeviceOperationId, setSelectedDeviceOperationId] = useState<string | number>('');
  const [selectedPriority, setSelectedPriority] = useState<number>(0);

  //상세보기 + 수정용
  const initialScenarioInputForm: ScenarioInputForm = {
    scenario_id: '',
    name: '',
    description: '',
    site_id: '',
    site_name: '',
    road_id: '',
    road_name: '',
    facility_id: '',
    facility_name: '',
    detection_id: '',
    detection_name: '',
    device_operation_id: '',
    device_operation_name: '',
    priority: 0,
    registered_by: '',
    registered_time: '',
    updated_by: '',
    updated_time: '',
  };
  //상세보기 + 수정용
  const [scenarioInputForm, setScenarioInputForm] =
    useState<ScenarioInputForm>(initialScenarioInputForm);

  const { commonListData } = useCommonCodes({ optionParams });

  const { siteListData } = useSiteManagement({ siteId: null });

  const queryParamsRoad = siteId ? `site_id=${siteId}` : '';

  const { roadListData } = useRoadManagement({
    queryParams: queryParamsRoad,
  }); //사이트 아이디에 맞는 접근로를 가져왔음

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked]);

  //상단 검색 필터를 위한 옵션
  const siteFilterOptions: OptionItem[] = [
    { value: '', label: '전체' }, // 기본값(전체)
    ...(siteListData?.data || []).map((site) => ({
      value: site.site_id,
      label: site.name,
    })),
  ];

  const roadFilterOptions: OptionItem[] = [
    { value: '', label: '전체' }, // 기본값(전체)
    ...(roadListData?.data || []).map((road) => ({
      value: road.road_id,
      label: road.name,
    })),
  ];

  //시설물 필터 옵션 - 스피커(221001), 전광판(221002)
  const facilityTypeFilterOptions: OptionItem[] = [
    { value: '', label: '전체' }, // 기본값(전체)
    ...(commonListData?.['221']?.code || []).map((code, index) => ({
      value: code ?? '',
      label: commonListData?.['221']?.name[index] ?? '',
    })),
  ];

  const [selectedScenario, setSelectedScenario] = useState<SelectedScenario>({
    scenario_id: null,
  });

  const table = useServerTable<{
    facility_type?: string;
    input?: string;
    site_id?: string;
    road_id?: string;
  }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;
  const [unmappedQueryParams, setUnmappedQueryParams] = useState<string>('resource=scenario');

  const updateCallback = () => {
    enableUpdateButtons();
    setHasChangesUpdate(false);
  };

  const { scenarioListData, detailScenarioData, createScenario, deleteScenario, updateScenario } =
    useScenarioManagement({
      scenarioId: selectedScenario?.scenario_id ?? undefined,
      queryParams: queryParams,
      onCreateSuccess: (responseData) => {
        const newScenarioId = String(responseData.scenario_id);
        setNewId(newScenarioId);
        setSelectedScenario({ scenario_id: newScenarioId });
        setIsNewClicked(false);
        setHasChangesCreate(false);
        enableUpdateButtons();
        setDisabledForm(false);
      },
      onUpdateSuccess: () => {
        updateCallback();
        //alert('수정 성공')
      },
      onDeleteSuccess: () => {
        //alert('삭제 성공')
        setSelectedScenario({
          scenario_id: null,
        });
        reloadCallback();
      },
    });

  const scenarioPageData = scenarioListData?.data;
  const scenarioRows = scenarioPageData?.rows ?? [];
  const scenarioColumns: ColumnDefinition[] = useMemo(
    () => [
      {
        title: 'No',
        // Reverse numbering (newest row shows the highest), using the server total.
        formatter: (cell) => {
          const total = scenarioPageData?.total ?? 0;
          const globalIndex = (table.page - 1) * table.size + cell.getRow().getPosition(true);
          return String(total - globalIndex + 1);
        },
        width: 60,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '시나리오 ID',
        field: 'scenario_id',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnSettings,
      },
      {
        title: '사이트',
        field: 'site_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '접근로',
        field: 'road_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '시설물',
        field: 'facility_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '시나리오명',
        field: 'name',
        widthGrow: 2,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '이벤트',
        field: 'detection_name',
        widthGrow: 1.5,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '업데이트 일시',
        field: 'updated_time',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnSettings,
      },
    ],
    [scenarioPageData?.total, table.page, table.size]
  );

  // unmappedSiteRoad 가져오기
  const { unmappedSiteRoad } = useUnmappedSiteRoad({
    resource: unmappedQueryParams,
  });

  // unmappedSiteRoad 응답 처리
  useEffect(() => {
    if (unmappedSiteRoad) {
      setUnmappedSiteRoadList(unmappedSiteRoad.data.sites as unknown as Site[]); // API response includes full Site structure beyond the base `sites` type
      // setUnmappedInputSiteList(unmappedSiteRoad.data.sites)
    }
  }, [unmappedSiteRoad]);

  // 시나리오 선택 또는 생성 여부에 따라 쿼리 파라미터 설정
  useEffect(() => {
    const scenarioId = selectedScenario?.scenario_id;

    if (isNewClicked) {
      setUnmappedQueryParams('resource=scenario');
    } else if (scenarioId) {
      const query = `id=${scenarioId}&resource=scenario`;

      setUnmappedQueryParams(query);
    }
  }, [isNewClicked, selectedScenario]);

  const handleSiteChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = parseInt(e.target.value, 10);
    setSelectedSiteId(value);

    setSelectedRoadId(''); // 접근로 초기화
    setSelectedFacilityId(''); // 시설물 초기화
    setSelectedDetectionId([]); // 이벤트 초기화
    setSelectedDeviceOperationId(''); // 동작설정 초기화
    setSelectedPriority(0); // 우선 순위 초기화

    if (isNewClicked !== true) {
      setHasChangesUpdate(true);
    } else {
      setHasChangesCreate(true);
    }
  };

  const handleRoadChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = parseInt(e.target.value, 10);
    setSelectedRoadId(value);

    if (isNewClicked !== true) {
      setHasChangesUpdate(true);
    } else {
      setHasChangesCreate(true);
    }
  };

  const handleFacilityChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = parseInt(e.target.value, 10);
    setSelectedFacilityId(value);

    setSelectedDetectionId([]); // 이벤트 초기화
    setSelectedDeviceOperationId(''); // 동작설정 초기화
    setSelectedPriority(0); // 우선 순위 초기화

    if (isNewClicked !== true) {
      setHasChangesUpdate(true);
    } else {
      setHasChangesCreate(true);
    }
  };

  const handleDetectionChange = (selectedNames: string[]) => {
    // SMTR 방식: 선택된 이름 배열을 detection_id 배열로 변환
    const detectionIds = selectedNames
      .map((name) => {
        const detection = detectionList.find((d) => d.name === name);
        return detection?.detection_id;
      })
      .filter((id): id is number => id !== undefined);

    setSelectedDetectionId(detectionIds);

    if (isNewClicked !== true) {
      setHasChangesUpdate(true);
    } else {
      setHasChangesCreate(true);
    }
  };

  const handleDeviceOperationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = parseInt(e.target.value, 10);
    setSelectedDeviceOperationId(value);

    if (isNewClicked !== true) {
      setHasChangesUpdate(true);
    } else {
      setHasChangesCreate(true);
    }
  };

  const handlePriorityChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = parseInt(e.target.value, 10);
    setSelectedPriority(value);

    if (isNewClicked !== true) {
      setHasChangesUpdate(true);
    } else {
      setHasChangesCreate(true);
    }
  };

  const logData = detailScenarioData?.data
    ? [
        {
          label: t('cmn > registered by'),
          value: detailScenarioData?.data.registered_by || '',
        },
        {
          label: t('cmn > registered time'),
          value: detailScenarioData?.data.registered_time || '',
        },
        {
          label: t('cmn > updated by'),
          value: detailScenarioData?.data.updated_by || '',
        },
        {
          label: t('cmn > updated time'),
          value: detailScenarioData?.data.updated_time || '',
        },
      ]
    : [];

  useEffect(() => {
    if (detailScenarioData) {
      const {
        scenario_id = '',
        name = '',
        description = '',
        site_id = '',
        site_name = '',
        road_id = '',
        road_name = '',
        facility_id = '',
        facility_name = '',
        detection_id = '',
        detection_name = '',
        device_operation_id = '',
        device_operation_name = '',
        priority = 0,
        registered_by = '',
        registered_time = '',
        updated_by = '',
        updated_time = '',
      } = detailScenarioData.data || {};

      setScenarioInputForm({
        scenario_id,
        name,
        description,
        site_id,
        site_name,
        road_id,
        road_name,
        facility_id,
        facility_name,
        detection_id,
        detection_name,
        device_operation_id,
        device_operation_name,
        priority,
        registered_by,
        registered_time,
        updated_by,
        updated_time,
      });

      setSelectedSiteId(site_id);
      setSelectedRoadId(road_id);
      setSelectedFacilityId(facility_id);
      // detection_id가 배열로 올 수 있으므로 배열로 처리
      setSelectedDetectionId(Array.isArray(detection_id) ? detection_id : []);
      setSelectedDeviceOperationId(device_operation_id);
      setSelectedPriority(priority);
    }
  }, [detailScenarioData]);

  const handlesiteIdFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setsiteId(e.target.value);
  };
  const handleRoadTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRoadId(e.target.value);
  };

  //
  const handleFacilityTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFacilityType(e.target.value);
  };

  const handleSearchInit = (inputVal: string = '', _radio?: string, _radioUsage?: string) => {
    table.setFilters({
      facility_type: facilityType || undefined,
      input: inputVal || undefined,
      site_id: siteId || undefined,
      road_id: roadId || undefined,
    });
  };

  const handleSearchReset = () => {
    //alert('재설정!')
    setsiteId('');
    setRoadId('');
    setFacilityType('');
    table.reset();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: t and enableUpdateButtons are stable and don't need to be dependencies
  const handleRowSelected = useCallback((row: Tabulator.RowComponent) => {
    if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('입력하신 내용이 저장되지 않습니다. 계속하시겠습니까?'), {
        mode: 'confirm',
      });
      message
        .confirmClicked()
        .then(() => {
          tbRef.current?.deselectRow();
          row.select();
          row.getElement().classList.add('custom-hover');

          const rowData = row.getData();
          setSelectedScenario({
            scenario_id: String(rowData.scenario_id),
          });
          setDisabledForm(false);
          enableUpdateButtons();
          setHasChangesUpdate(false);
          setIsNewClicked(false);
        })
        .catch(() => {
          row.deselect();
        });
    } else {
      tbRef.current?.deselectRow();
      row.select();
      row.getElement().classList.add('custom-hover');

      const rowData = row.getData();
      setSelectedScenario({
        scenario_id: String(rowData.scenario_id),
      });
      setDisabledForm(false);
      enableUpdateButtons();
      setIsNewClicked(false);
    }

    requestAnimationFrame(() => {
      if (nameRef.current) {
        nameRef.current.focus();
      }
    });
  }, []);

  const handleScenarioFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setScenarioInputForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (isNewClicked !== true) {
      setHasChangesUpdate(true);
    }
    // David
    else {
      setHasChangesCreate(true);
    }
  };

  const emptyDetail = () => {
    //alert('비우기!')
    setScenarioInputForm(initialScenarioInputForm);

    setSelectedSiteId('');
    setSelectedRoadId('');
    setSelectedFacilityId('');
    setSelectedDetectionId([]);
    setSelectedDeviceOperationId('');
    setSelectedPriority(0);
  };

  //Button
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = () => {
    //alert('버튼 비활성화')
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  };

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  // const enableUpudateButtons = () => {
  //   disableAllButtons();
  //   setButtonState((prevState) => ({
  //     ...prevState,
  //     cancel: false,
  //     delete: false,
  //     create: false,
  //   }));
  // };

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  const enableCreateButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      create: true,
    }));
  };

  const enableCancelButtons = () => {
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  //수정관련 버튼 활성화
  const enableUpdateButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false, //취소 버튼 활성화
      delete: false, //삭제 버튼 활성화
      create: false, //생성 버튼 활성화
    }));
  }; //disable-false는 활성화

  const reloadCallback = () => {
    enableInitialButtons();
    //tbRef.current.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedScenario({ scenario_id: null });
    //setSelectedDevice({ device_operation_id: null });
  };

  const handleDeleteButtonClick = () => {
    if (!selectedScenario?.scenario_id) {
      return;
    }
    //alert('삭제!');

    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('정말로 삭제하시겠습니까?'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      //alert(deviceInputForm.mapped_scenario_name)

      const { scenario_id } = scenarioInputForm;
      //alert('삭제 진행!:'+scenario_id)
      deleteScenario(scenario_id);

      // reloadCallback()
    });
  };

  const handleNewButtonClick = () => {
    //alert('생성!')
    if (hasChangesUpdate || hasChangesCreate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setDisabledForm(false);
        enableCreateButtons();
        enableRegisterButtons();
        setIsNewClicked(true); //등록으로 변경
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      enableRegisterButtons();
      enableCreateButtons();
      setDisabledForm(false);
      enableCancelButtons();
      setIsNewClicked(true); //등록으로 변경
    }

    setIsNewClicked(true); //등록으로 변경
    setHasChangesUpdate(false); //원래 변경안됨 상태로 초기화
    setSelectedScenario({ scenario_id: null });
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });

      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setDisabledForm(true);
        enableInitialButtons();
        setSelectedScenario({ scenario_id: null });
        setHasChangesUpdate(false);
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });

        message.confirmClicked().then(() => {
          tbRef.current?.deselectRow();
          emptyDetail();
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesUpdate(false);
          setSelectedScenario({ scenario_id: null });
          setHasChangesCreate(false);
        });
      } else {
        tbRef.current?.deselectRow();
        emptyDetail();
        setDisabledForm(true);
        enableInitialButtons();
        setSelectedScenario({ scenario_id: null });
        setHasChangesUpdate(false);
        setHasChangesCreate(false);
      }
    } else {
      reloadCallback();
      tbRef.current?.deselectRow();
      setDisabledForm(true);
    }
  };

  const handleRegistButtonClick = () => {
    //alert('등록!')

    const { name, description } = scenarioInputForm;
    // device_type, name, description 값이 모두 입력되었는지 확인
    if (
      name.length === 0 ||
      selectedSiteId.toString().length === 0 ||
      selectedRoadId.toString().length === 0 ||
      selectedFacilityId.toString().length === 0 ||
      selectedDetectionId.length === 0 ||
      selectedDeviceOperationId.toString().length === 0 ||
      selectedPriority === null ||
      selectedPriority === undefined
    ) {
      // 값이 하나라도 입력되지 않으면 경고창을 띄우고 진행하지 않음
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage('필수 값을 모두 입력해주세요');
      checkRequiredFields();
      return; // 진행을 중단
    }

    const createScenarioObj = {
      site_id: selectedSiteId,
      road_id: selectedRoadId,
      facility_id: selectedFacilityId,
      detection_id: selectedDetectionId,
      device_operation_id: selectedDeviceOperationId,
      priority: selectedPriority,
      name: name,
      description: description,
    };

    createScenario(createScenarioObj);

    // reloadCallback()
    // setDisabledForm(true)
  };

  const handleConfirmButtonClick = () => {
    //alert('확인')

    const { scenario_id, name, description } = scenarioInputForm;

    // device_type, name, 값이 모두 입력되었는지 확인
    // description은 필수값이 아님
    if (
      name.length === 0 ||
      selectedSiteId.toString().length === 0 ||
      selectedRoadId.toString().length === 0 ||
      selectedFacilityId.toString().length === 0 ||
      selectedDetectionId.length === 0 ||
      selectedDeviceOperationId.toString().length === 0 ||
      selectedPriority === null ||
      selectedPriority === undefined
    ) {
      // 값이 하나라도 입력되지 않으면 경고창을 띄우고 진행하지 않음
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage('필수 값을 모두 입력해주세요');
      checkRequiredFields();
      return; // 진행을 중단
    }

    const updateObj = {
      scenario_id: scenario_id,
      site_id: selectedSiteId,
      road_id: selectedRoadId,
      facility_id: selectedFacilityId,
      detection_id: selectedDetectionId,
      device_operation_id: selectedDeviceOperationId,
      priority: selectedPriority,
      name: name,
      description: description,
    };

    updateScenario(updateObj);
  };

  const checkRequiredFields = () => {
    const requiredFields = [
      { name: 'name', value: name },
      { name: 'description', value: description },
      { name: 'selectedSiteId', value: selectedSiteId },
      { name: 'selectedRoadId', value: selectedRoadId },
      { name: 'selectedFacilityId', value: selectedFacilityId },
      { name: 'selectedDetectionId', value: selectedDetectionId },
      { name: 'selectedDeviceOperationId', value: selectedDeviceOperationId },
      { name: 'selectedPriority', value: selectedPriority },
    ];

    // 비어있는 값 체크
    const emptyFields = requiredFields.filter(
      (field) => field.value === '' || field.value === null
    );

    if (emptyFields.length > 0) {
      // 비어있는 필드 출력Back To Here

      emptyFields.forEach((_field) => {
        //alert(`${field.name} is empty`);
      });
      return true; // 필수 값이 하나라도 비어있으면 true 반환
    }

    return false; // 모든 필드가 채워져 있으면 false 반환
  };

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    index: 'id',
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    movableRows: false,
    resizableRows: false,
    selectableRows: false, //이 옵션을 줘야 로우 선택이 된다.
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };

  const { scenario_id, name, description } = scenarioInputForm || {};

  siteListData?.data?.forEach(() => {});

  // 생성시에는 사용할 unmapped만
  // 상세보기에는 상세보기데이터+unmapped

  //하단 입력폼

  //현재 사이트 옵션
  const unMappedSites = (unmappedSiteRoad?.data?.sites ?? []) as unknown as Site[]; // API response includes full Site structure beyond the base `sites` type
  //생성시에 나오는 옵션 - unmapped
  const createSiteOptions: OptionItem[] = [
    ...unMappedSites.map((site) => ({
      value: site.site_id,
      label: site.name,
    })),
  ];

  //상세보기시에 나오는 옵션 - unmapped + 시나리오아이디 관련 (선택)
  const detailViewSiteOptions: OptionItem[] = [
    ...unMappedSites.map((site) => ({
      value: site.site_id,
      label: site.name,
    })),
  ];

  const currentSiteOptions = isNewClicked ? createSiteOptions : detailViewSiteOptions;
  //선택한 사이트 정보를 저장 (접근로 정보를 가져오기 위함)
  const selectedSite = unMappedSites?.find((site) => site.site_id === selectedSiteId);

  //현재 접근로 옵션
  //생성시에 나오는 옵션 - unmapped
  const createRoadOptions: OptionItem[] = [
    ...(selectedSite?.roads || []).map((road) => ({
      value: road.road_id,
      label: road.name,
    })),
  ];

  //상세보기시에 나오는 옵션 - unmapped + 시나리오아이디 관련 (선택)
  const detailViewRoadOptions: OptionItem[] = [
    ...(selectedSite?.roads || []).map((road) => ({
      value: road.road_id,
      label: road.name,
    })),
  ];

  const currentRoadOptions = isNewClicked ? createRoadOptions : detailViewRoadOptions;

  //선택한 접근로 정보를 저장 (시설물 정보를 가져오기 위함)
  const selectedRoad = selectedSite?.roads.find((road) => road.road_id === selectedRoadId);
  //현재 시설물 옵션
  const createFacilityOptions: OptionItem[] = [
    ...(selectedRoad?.facilities || []).map((facility) => ({
      value: facility.facility_id,
      label: facility.name,
    })),
  ];

  //상세보기시에 나오는 옵션 - unmapped + 시나리오아이디 관련 (선택)
  const detailViewFacilityOptions: OptionItem[] = [
    ...(selectedRoad?.facilities || []).map((facility) => ({
      value: facility.facility_id,
      label: facility.name,
    })),
  ];

  const currentFacilityOptions = isNewClicked ? createFacilityOptions : detailViewFacilityOptions;

  const selectedFacility = selectedRoad?.facilities.find(
    (facility) => facility.facility_id === selectedFacilityId
  );

  //현재 이벤트 옵션 (이벤트, 즉 detection)
  //사이트->접근로->시설물->detections
  const detectionList = selectedFacility?.detections || [];

  // 생성 시에 나오는 옵션 - unmapped
  const createDetectionOptions: OptionItem[] = [
    ...detectionList.map((detection) => ({
      value: detection.detection_id,
      label: detection.name,
    })),
  ];

  // 상세보기 시에 나오는 옵션 - unmapped + 시나리오아이디 관련 (선택)
  const detailViewDetectionOption: OptionItem[] = [
    ...detectionList.map((detection) => ({
      value: detection.detection_id,
      label: detection.name,
    })),
  ];

  const _detectionOptions = isNewClicked ? createDetectionOptions : detailViewDetectionOption;

  // MultiSelect용 이벤트 옵션 - SMTR 방식: value도 이름으로 설정
  const detectionOptionsForMultiSelect = detectionList.map((detection) => ({
    item: detection.name,
    value: detection.name,
  }));

  // 현재 선택된 detection_id 배열을 이름 배열로 변환 (MultiSelect value용)
  const selectedDetectionNames = selectedDetectionId
    .map((id) => {
      const detection = detectionList.find((d) => d.detection_id === id);
      return detection?.name || '';
    })
    .filter((name) => name !== '');

  //현재 동작설정 옵션
  //사이트->접근로->시설물->device_operations
  const deviceOperationList = selectedFacility?.device_operations || [];

  const createDeviceOperationOptions: OptionItem[] = [
    ...deviceOperationList.map((device_operation) => ({
      value: device_operation.device_operation_id,
      label: device_operation.name,
    })),
  ];

  const detailViewDeviceOperationOptions: OptionItem[] = [
    ...deviceOperationList.map((device_operation) => ({
      value: device_operation.device_operation_id,
      label: device_operation.name,
    })),
  ];

  const deviceOperationOptions = isNewClicked
    ? createDeviceOperationOptions
    : detailViewDeviceOperationOptions;

  // 우선 순위 옵션 (0-100)
  const priorityOptions: OptionItem[] = Array.from({ length: 101 }, (_, i) => ({
    value: i,
    label: String(i),
  }));

  //
  return (
    <section className="wrap">
      <div className="header-title">
        <h3>시나리오 관리</h3>
        <h3>&gt;</h3>
        <h3> 시나리오 관리</h3>
      </div>

      <ContainerCard>
        <Filtering
          placeholder="시나리오명, 이벤트"
          disableFiltering={true}
          showLabel={false}
          disableResetButton={false}
          onReset={handleSearchReset}
          onSearch={handleSearchInit}
          customWidthSelect="flex flex-1 basis-[700px]"
          customWidthInput="flex flex-1"
        >
          <div className="flex flex-row gap-[15px] w-full">
            <div className="flex w-full gap-[15px]">
              <span className="title3bold w-[97px] text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
                사이트
              </span>
              <Select
                value={siteId}
                options={siteFilterOptions as { value: string; label: string }[]}
                onChange={(e) => handlesiteIdFilterChange(e)}
                customWidthSelect="w-full"
                className="cursor-pointer"
              />
            </div>
            <div className="flex w-full gap-[15px]">
              <span className="title3bold w-[97px] text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
                접근로
              </span>
              <Select
                value={roadId}
                options={roadFilterOptions as { value: string; label: string }[]}
                onChange={(e) => handleRoadTypeFilterChange(e)}
                customWidthSelect="w-full"
                disabled={!siteId}
              />
            </div>
            <div className="flex w-full gap-[15px]">
              <span className="title3bold w-[97px] text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
                시설물 유형
              </span>
              <Select
                value={facilityType}
                options={
                  facilityTypeFilterOptions as {
                    value: string;
                    label: string;
                  }[]
                }
                onChange={(e) => handleFacilityTypeFilterChange(e)}
                customWidthSelect="w-full"
              />
            </div>
          </div>
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <ReactTabulator
          data={scenarioRows}
          columns={scenarioColumns}
          layout={'fitColumns'}
          className="tabulator-custom w-full"
          id="scenario-management-table"
          options={optionsTabulator}
          onRef={(r) => {
            tbRef.current = r.current;
          }}
          events={{
            rowClick: (_e: UIEvent, row: Tabulator.RowComponent) => handleRowSelected(row),
            // Header click → delegate sorting to the server (see useServerTable).
            dataSorted: remoteSortHandler(table, SCENARIO_SORT_FIELD_MAP),
            tableBuilt: () => {
              if (selectedScenario?.scenario_id) {
                tbRef.current?.getRows().forEach((row) => {
                  if (String(row.getData().scenario_id) === selectedScenario.scenario_id) {
                    row.select(); // scenario_id 가 일치하는 행을 선택
                    row.getElement().classList.add('custom-hover');
                  }
                });
              } else if (newId) {
                //alert('새로운 아이디 선택!')
                //alert(newId)

                tbRef.current?.getRows().forEach((row) => {
                  const rowData = row.getData();
                  if (String(rowData.scenario_id) === newId) {
                    row.select(); // scenario_id 가 일치하는 행을 선택
                    row.getElement().classList.add('custom-hover');
                    tbRef.current?.selectRow(newId as any);
                    handleRowSelected(row);
                    //setDisabledForm(true)
                  }
                });
              }
            },
          }}
        />
        <TablePagination
          total={scenarioPageData?.total ?? 0}
          page={table.page}
          size={table.size}
          totalPages={scenarioPageData?.total_pages ?? 0}
          onPageChange={table.setPage}
        />
      </ContainerCard>

      <ContainerCard className="flex flex-col">
        <div className="box-scenario-event flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="시나리오 ID"
              value={scenario_id || ''}
              disabled={true}
              name={'scenario_id'}
            />

            <DetailForm
              className="items-center!"
              label="시나리오명"
              value={name || ''}
              disabled={disabledForm}
              onChange={(e) => handleScenarioFormChange(e)}
              name={'name'}
              required={true}
            />

            <DetailForm
              inputType="select"
              className="items-center!"
              label="사이트"
              required={true}
              disabled={disabledForm}
              optionSelect={currentSiteOptions as { value: string; label: string }[]}
              onChange={(e) => handleSiteChange(e)}
              value={selectedSiteId}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              inputType="select"
              className="items-center!"
              label="접근로"
              required={true}
              disabled={disabledForm}
              optionSelect={currentRoadOptions as { value: string; label: string }[]}
              onChange={(e) => handleRoadChange(e)}
              value={selectedRoadId}
            />
            <DetailForm
              inputType="select"
              className="items-center!"
              label="시설물"
              required={true}
              disabled={disabledForm}
              optionSelect={currentFacilityOptions as { value: string; label: string }[]}
              onChange={(e) => handleFacilityChange(e)}
              value={selectedFacilityId}
            />

            <DetailForm label="이벤트" showInput={false} required={true}>
              <MultiSelect
                options={detectionOptionsForMultiSelect}
                disabled={disabledForm}
                placeholder="이벤트 선택"
                value={selectedDetectionNames}
                onChange={(value) => handleDetectionChange(value)}
              />
            </DetailForm>
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              inputType="select"
              className="items-center!"
              label="동작 설정"
              required={true}
              disabled={disabledForm}
              optionSelect={deviceOperationOptions as { value: string; label: string }[]}
              onChange={(e) => handleDeviceOperationChange(e)}
              value={selectedDeviceOperationId}
            />
            <DetailForm
              inputType="select"
              className="items-center!"
              label="우선 순위"
              required={true}
              disabled={disabledForm}
              optionSelect={priorityOptions as { value: string; label: string }[]}
              onChange={(e) => handlePriorityChange(e)}
              value={selectedPriority}
            />
          </div>

          <div className="grid grid-cols-1">
            <DetailForm
              className=" h-[90px]"
              inputType="textarea"
              label="시나리오 설명"
              disabled={disabledForm}
              value={description || ''}
              onChange={(e) => handleScenarioFormChange(e)}
              name={'description'}
              maxLength={500}
            />
          </div>

          <hr className="border-t border-gray-300" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">
              <LogList logs={logData} />
            </div>
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default ScenarioManagement;
